/**
 * Column layout for the roster, shared by the header and every row so the two
 * cannot drift apart.
 *
 * Columns: Name, Next Appointment, Last Engaged, plus a chevron. Round 6 cut
 * this back to three from five at the team's request — the Summary view is for
 * scanning "who am I seeing, and who has gone quiet", and everything else
 * belonged on the patient profile.
 *
 * What went, and why the filters didn't go with them:
 *  - **Risk Stratification** (round 3) — column only.
 *  - **Care Coordination** (round 7) — the funnel stage. Its recency half is
 *    what people actually scanned the column for, and that is now the Last
 *    Engaged column below; the stage itself stays filterable in the sidebar.
 *  - **Outcome Goal** (round 7, one round after it was added) — still
 *    filterable, so "show me who's worsening" works; it just no longer costs
 *    every row a column.
 *
 * Each removal keeps its sidebar filter, following the precedent Risk
 * Stratification set: a column and a filter answer different questions, and
 * cutting the column is not a request to stop filtering on the thing.
 */
// Ratios track what each column's widest content actually measures, so the
// floor below divides up cleanly: next appointment ~245px (date + time + the
// confirmation pill, all on one line), last engaged ~110px ("Aug 10"), and the
// name column takes the remainder — it's the one that truncates gracefully,
// and with two columns gone it has room to do so less often.
export const ROW_GRID = '3.4fr 3.3fr 1.6fr 28px';

/**
 * Narrowest the row can get before its cells start colliding. The list
 * scrolls sideways inside itself rather than squeezing past it.
 *
 * Down from 880: the two columns that set most of that floor are gone. What
 * remains is next appointment (~245px — date, time and the confirmation pill
 * on one line, none of which may wrap) and last engaged (~110px), plus the row
 * gutter, the chevron, the gaps, and enough name column to be worth reading.
 */
export const ROW_MIN_WIDTH = 620;

/**
 * Horizontal padding (Chakra spacing token) on the header and every row.
 *
 * This is the list column's whole gutter, not just a cell inset: the column
 * itself is flush to the frame so the header's bottom border can run edge to
 * edge, and each row insets its own content instead. Exported so anything
 * that needs to line up with the Name column — the search box above the list
 * — uses the same number rather than guessing.
 */
export const ROW_PADDING_X = 6;

/**
 * The appointment column's header changes with the filter.
 *
 * By default and in the forward-looking buckets the cell only ever shows an
 * upcoming appointment, so "Next Appointment" is accurate. Select a past
 * window and every visible appointment is one that already happened, which
 * makes "Next" simply wrong — so the header follows the data rather than
 * staying put and lying in the two modes round 7 added. See
 * `displayedAppointment`.
 */
export const APPOINTMENT_HEADER = {
  upcoming: 'Next Appointment',
  past: 'Last Appointment',
} as const;

export const columnHeaders = (isPast: boolean) => [
  'Name',
  isPast ? APPOINTMENT_HEADER.past : APPOINTMENT_HEADER.upcoming,
  'Last Engaged',
  '',
];
